import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from './store';
import OrganizationService from '../services/OrganizationService';
import { IOrganizationRequest, IOrganizationResponse } from '../dashboard/organization/OrganizationList';
// import { IOrganizationRequest, IOrganizationResponse } from "@ontime/types";

// Function to load state from localStorage
const loadState = (): OrganizationUserState => {
    try {
        const serializedState = localStorage.getItem('UserOrganizationsList');
        return serializedState ? JSON.parse(serializedState) : { organizationUsers: [], loading: false, error: null };
    } catch (error) {
        console.error('Could not load place users state:', error);
        return { organizationUsers: [], loading: false, error: null };
    }
};

// Function to save state to localStorage
const saveState = (state: OrganizationUserState) => {
    try {
        localStorage.setItem('UserOrganizationsList', JSON.stringify(state));
    } catch (error) {
        console.error('Could not save place users state:', error);
    }
};

// Async thunk for fetching organization users
export const fetchUserOrganizationsList = createAsyncThunk(
    'organizationUsers/fetchAll',
    async (showDeleted: boolean = false) => {
        try {
            return await OrganizationService.GetUserOrganizationsList(showDeleted);
        } catch (error) {
            return null;
        }
    }
);

export const createOrganizationAsync = createAsyncThunk(
    'organizationUsers/create',
    async (data: IOrganizationRequest) => {
        try {
            return await OrganizationService.CreateOrganization(data);
        } catch (error) {
            return null;
        }
    }
);

export const updateOrganizationAsync = createAsyncThunk(
    'organizationUsers/update',
    async (
        { organizationId, data }: { organizationId: string; data: IOrganizationRequest }
    ) => {
        try {
            return await OrganizationService.UpdateOrganization(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const deactivateOrganizationAsync = createAsyncThunk(
    'organizationUsers/deactivate',
    async (organizationId: string) => {
        try {
            return await OrganizationService.DeactivateOrganization(organizationId);
        } catch (error) {
            return null;
        }
    }
);

export const activateOrganizationAsync = createAsyncThunk(
    'organizationUsers/activate',
    async (organizationId: string) => {
        try {
            return await OrganizationService.ActivateOrganization(organizationId);
        } catch (error) {
            return null;
        }
    }
);

export const deleteOrganizationAsync = createAsyncThunk(
    'organizationUsers/delete',
    async (organizationId: string) => {
        try {
            return await OrganizationService.DeleteOrganization(organizationId);
        } catch (error) {
            return null;
        }
    }
);

interface OrganizationUserState {
    organizationUsers: IOrganizationResponse[];
    loading: boolean;
    error: string | null;
}

const initialState: OrganizationUserState = loadState();

const organizationUserAWSSlice = createSlice({
    name: 'UserOrganizationsList',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserOrganizationsList.pending, (state) => {
                state.organizationUsers = [];
                state.loading = true;
                state.error = null;
                saveState(state);
            })
            .addCase(fetchUserOrganizationsList.fulfilled, (state, action) => {
                state.loading = false;
                state.organizationUsers = action.payload ?? [];
                saveState(state);
            })
            .addCase(fetchUserOrganizationsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                saveState(state);
            })
            .addCase(createOrganizationAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.organizationUsers.unshift(action.payload);
                    saveState(state);
                }
            })
            .addCase(updateOrganizationAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.organizationUsers.findIndex(o => o.organizationId === action.payload?.organizationId);
                    if (idx !== -1) {
                        state.organizationUsers[idx].organizationName = action.payload.name;
                        saveState(state);
                    }
                }
            })
            .addCase(deactivateOrganizationAsync.fulfilled, (state, action) => {
                const idx = state.organizationUsers.findIndex(o => o.organizationId === action.payload?.organizationId);
                if (idx !== -1) {
                    state.organizationUsers[idx] = { ...state.organizationUsers[idx], organizationIsActivated: false };
                    saveState(state);
                }
            })
            .addCase(activateOrganizationAsync.fulfilled, (state, action) => {
                const idx = state.organizationUsers.findIndex(o => o.organizationId === action.payload?.organizationId);
                if (idx !== -1) {
                    state.organizationUsers[idx] = { ...state.organizationUsers[idx], organizationIsActivated: true };
                    saveState(state);
                }
            })
            .addCase(deleteOrganizationAsync.fulfilled, (state, action) => {
                state.organizationUsers = state.organizationUsers.filter(sp => sp.organizationId !== action.payload?.organizationId);
                saveState(state);
            })
    }
});

export default organizationUserAWSSlice.reducer;
export const selectUserOrganizationsList = (state: RootState) => state.organizationUsers.organizationUsers;
export const selectUserOrganizationsListLoading = (state: RootState) => state.organizationUsers.loading;
export const selectUserOrganizationsListError = (state: RootState) => state.organizationUsers.error;